import { LibraryPart } from "@/components/library/LibraryPart";
import { AuthContext } from "@/context/AuthContext";
import ConductionAPI from "@/javascript/conduction-api";
import { Song } from "@/javascript/library-objects";
import MidiPlayer, { MidiPlayerTrack } from "@/javascript/midi-player";
import { Key, PartList } from "@/javascript/music-objects";
import { Utils } from "@/javascript/utils";
import {
	Box,
	Button,
	Group,
	Loader,
	Modal,
	Paper,
	Text
} from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import React, {
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import ConductionModal from "../ConductionModal";

// Constants (matching the ones in your class version)
const MIN_MEASURE_WIDTH = 15;
const MAX_MEASURE_WIDTH = 80;
const DEFAULT_ZOOM = 3;
const ZOOM_INTERVALS = 10;
const ZOOM_FOCALPOINT_EDGE_SNAP_THRESHOLD = 200;
const MIN_PART_HEIGHT = 40;
const MAX_PART_HEIGHT = 60;
const SECTION_LABEL_HEIGHT = 30;
const PART_LABEL_WIDTH = 100;

interface SongProcessorProps {
	song: Song;
	onClose: () => void;
	onProcessed: () => void;
}

const SongProcessor: React.FC<SongProcessorProps> = ({ song, onClose, onProcessed }) => {
	// -----------------------
	// State and Refs
	// -----------------------
	const [songPartList, setSongPartList] = useState<PartList | null | undefined>(undefined);
	// selectedSectionsIds is now an array of strings.
	const [selectedSectionsIds, setSelectedSectionsIds] = useState<string[]>([]);
	const [measureWidth, setMeasureWidth] = useState<number>(
		MIN_MEASURE_WIDTH + ((MAX_MEASURE_WIDTH - MIN_MEASURE_WIDTH) * DEFAULT_ZOOM) / ZOOM_INTERVALS
	);
	const [partHeight, setPartHeight] = useState<number>(
		(DEFAULT_ZOOM / (ZOOM_INTERVALS / 2)) * (MAX_PART_HEIGHT - MIN_PART_HEIGHT) + MIN_PART_HEIGHT
	);
	// isLoading here refers to loading song part data or processing
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [errorMessage, setErrorMessage] = useState<string>("");
	const [isPlaying, setIsPlaying] = useState<boolean>(false);
	const [playingSectionName, setPlayingSectionName] = useState<string | null>(null);

	const librarySectionContainerRef = useRef<HTMLDivElement>(null);

	const auth = useContext(AuthContext);
	if (!auth) throw new Error("AdminSongProcessor must be a child of AuthContext.");
	const token = auth.token;
	if (!token) return null;

	const fetchUnprocessedSongData = useCallback(async () => {
		if (song === null) return;
		// Reset selections and song data
		setSelectedSectionsIds([]);
		setSongPartList(undefined);
		try {
			const partListOrErrorMessage = await ConductionAPI.getUnprocessedSongData(song.songId, token);
			if (typeof partListOrErrorMessage === "string") {
				setIsLoading(false);
				setErrorMessage(partListOrErrorMessage);
			} else {
				const partList = partListOrErrorMessage as PartList;
				partList.transpose(new Key(0));
				partList.setName(song.songName);
				setSongPartList(partList);
				setIsLoading(false);
			}
		} catch (error) {
			console.error("Error fetching song data:", error);
			setIsLoading(false);
			setErrorMessage("Failed to fetch song data.");
		}
	}, [song, token]);

	useEffect(() => {
		fetchUnprocessedSongData();
	}, [song, token])

	// -----------------------
	// Zoom Calculation and Handling
	// -----------------------
	const calculateCurrentZoom = useCallback((): number => {
		return (
			((measureWidth - MIN_MEASURE_WIDTH) / (MAX_MEASURE_WIDTH - MIN_MEASURE_WIDTH)) *
			ZOOM_INTERVALS
		);
	}, [measureWidth]);

	const updateZoom = useCallback(
		(newZoom: number, focalPointX?: number, focalPointY?: number) => {
			if (!songPartList || !librarySectionContainerRef.current) return;
			const libraryContainer = librarySectionContainerRef.current;
			const boundingRect = libraryContainer.getBoundingClientRect();
			const focalX =
				focalPointX !== undefined ? focalPointX - boundingRect.left : boundingRect.width / 2;
			const focalY =
				focalPointY !== undefined ? focalPointY - boundingRect.top : boundingRect.height / 2;
			const focalXRelative = focalX - PART_LABEL_WIDTH;
			const xHighSnapPoint =
				boundingRect.width - PART_LABEL_WIDTH - ZOOM_FOCALPOINT_EDGE_SNAP_THRESHOLD;
			const yHighSnapPoint = boundingRect.height - ZOOM_FOCALPOINT_EDGE_SNAP_THRESHOLD;
			const focalXRel =
				focalXRelative < ZOOM_FOCALPOINT_EDGE_SNAP_THRESHOLD
					? 0
					: focalXRelative > xHighSnapPoint
						? boundingRect.width - PART_LABEL_WIDTH
						: focalXRelative;
			const focalYRel =
				focalY < ZOOM_FOCALPOINT_EDGE_SNAP_THRESHOLD
					? 0
					: focalY > yHighSnapPoint
						? boundingRect.height
						: focalY;

			const currentZoom = calculateCurrentZoom();
			const zoomingOut = newZoom < currentZoom;
			newZoom = Math.round(newZoom);
			if (newZoom === currentZoom) {
				newZoom = zoomingOut ? newZoom - 1 : newZoom + 1;
			}
			newZoom = Math.max(0, Math.min(ZOOM_INTERVALS - 1, newZoom));
			const libraryWidth = measureWidth * songPartList.getLength();
			const focalXRatio = (libraryContainer.scrollLeft + focalXRel) / libraryWidth;
			const totalHeight = partHeight * songPartList.parts.length + SECTION_LABEL_HEIGHT;
			const focalYRatio = (libraryContainer.scrollTop + focalYRel) / totalHeight;

			const newMeasureWidth =
				MIN_MEASURE_WIDTH +
				((MAX_MEASURE_WIDTH - MIN_MEASURE_WIDTH) * newZoom) / ZOOM_INTERVALS;
			const halfwayZoomInterval = ZOOM_INTERVALS / 2;
			const newPartHeight =
				(newZoom / halfwayZoomInterval) * (MAX_PART_HEIGHT - MIN_PART_HEIGHT) + MIN_PART_HEIGHT;

			setMeasureWidth(newMeasureWidth);
			setPartHeight(newPartHeight);

			requestAnimationFrame(() => {
				const newLibraryWidth = newMeasureWidth * songPartList.getLength();
				libraryContainer.scrollLeft = focalXRatio * newLibraryWidth - focalXRel;
				const newTotalHeight =
					newPartHeight * songPartList.parts.length + SECTION_LABEL_HEIGHT;
				libraryContainer.scrollTop = Math.min(
					focalYRatio * newTotalHeight - focalYRel,
					libraryContainer.scrollHeight - libraryContainer.clientHeight
				);
				const libraryHeightMinusScroll = newTotalHeight - libraryContainer.scrollTop;
				if (zoomingOut && libraryHeightMinusScroll < libraryContainer.clientHeight) {
					libraryContainer.scrollTop -= libraryContainer.clientHeight - libraryHeightMinusScroll;
				}
			});
		},
		[calculateCurrentZoom, measureWidth, partHeight, songPartList]
	);

	const handleLibraryZoom = useCallback(
		(event: React.WheelEvent<HTMLDivElement>) => {
			event.stopPropagation();
			if (Math.abs(event.deltaY) > 1) {
				updateZoom(calculateCurrentZoom() - event.deltaY / 100, event.clientX, event.clientY);
			}
		},
		[updateZoom, calculateCurrentZoom]
	);

	// -----------------------
	// Section Selection and MIDI Playback
	// -----------------------
	const handleBoxSelect = useCallback(
		(sectionId: string) => {
			if (!songPartList) return;
			let newArray = [...selectedSectionsIds];
			if (newArray.includes(sectionId)) {
				newArray = newArray.filter((id) => id !== sectionId);
				if (isPlaying) {
					MidiPlayer.removeTrack(sectionId);
					if (MidiPlayer.getTrackCount() <= 0) MidiPlayer.stop();
				}
			} else {
				newArray = [...newArray, sectionId];
				const newSelectedPart = songPartList.parts.find((part) =>
					part.sections.some((section) => section.id === sectionId)
				);
				if (newSelectedPart) {
					const newSelectedSection = newSelectedPart.sections.find(
						(section) => section.id === sectionId
					);
					if (newSelectedSection && newSelectedSection.getLength() > 0) {
						const newSectionName = newSelectedSection.name;
						if (playingSectionName && playingSectionName === newSectionName) {
							if (newSelectedPart.instrument) {
								const newTrack: MidiPlayerTrack = {
									measures: newSelectedSection.toMidiMeasures(),
									instrumentName:
										newSelectedPart.instrument.soundfontInstrument as any,
									trackId: newSelectedSection.id,
									isPercussion: newSelectedPart.instrument.midiProgram === 0,
								};
								MidiPlayer.addTrack(newTrack);
							}
						} else {
							MidiPlayer.stop();
							setPlayingSectionName(newSectionName);
							const selectedSectionsMatchingName = songPartList.parts
								.flatMap((part) => part.sections)
								.filter(
									(section) =>
										newArray.includes(section.id) &&
										(section.name === newSectionName ||
											section.id === newSelectedSection.id)
								);
							const playLoopCallback = async () => { };
							const stopCallback = () => {
								setIsPlaying(false);
								setPlayingSectionName(null);
							};
							const removeTrackCallback = () => {
								if (MidiPlayer.getTrackCount() <= 0) {
									MidiPlayer.stop();
									setPlayingSectionName(null);
								}
							};
							MidiPlayer.setCallbacks(
								playLoopCallback,
								stopCallback,
								removeTrackCallback
							);
							Promise.all(
								selectedSectionsMatchingName.map(async (section) => {
									const instrument = section.getPart()?.instrument;
									if (!instrument) {
										console.error(`Part instrument not found for section: ${section}`);
										return;
									}
									const newTrack: MidiPlayerTrack = {
										measures: section.toMidiMeasures(),
										instrumentName: instrument.soundfontInstrument as any,
										trackId: section.id,
										isPercussion: instrument.midiProgram === 0,
									};
									await MidiPlayer.addTrack(newTrack);
								})
							).then(() => {
								MidiPlayer.play();
								setIsPlaying(true);
							});
						}
					}
				}
			}
			setSelectedSectionsIds(newArray);
		},
		[songPartList, selectedSectionsIds, isPlaying, playingSectionName]
	);

	// -----------------------
	// File Upload and Processing
	// -----------------------
	const handleMusicXMLFileChange = useCallback(
		async (file: File | null) => {
			if (song === null) return;
			if (!file) return;
			setIsLoading(true);
			try {
				await ConductionAPI.uploadMusicXML(song.songId, file, token);
				await fetchUnprocessedSongData();
			} catch (error: any) {
				setSongPartList(null);
				setErrorMessage(error.message);
				setIsLoading(false);
			}
		},
		[song, fetchUnprocessedSongData, token]
	);

	const processSongData = useCallback(async () => {
		if (song === null) return;
		if (!songPartList) return;
		setIsLoading(true);
		try {
			await ConductionAPI.processSongData(song.songId, token);
			Utils.displayNotification(
				"Success!",
				"Song data processed.",
				"green",
				<IconCheck size={16} />
			);
			onProcessed();
			onClose();
		} catch (error: any) {
			setErrorMessage(error.message);
			setIsLoading(false);
		}
	}, [songPartList, song, token, onClose, onProcessed]);

	const height = 80; // view height (vh)

	// -----------------------
	// Render
	// -----------------------
	return (
		<ConductionModal
			size="90%"
			onClose={onClose}
			title={`Song Processor: ${song ? song.songName : ""}`}
		>
			<Box
				style={{
					minHeight: `${height}vh`
				}}
			>
				<Modal
					opened={!!errorMessage}
					onClose={() => setErrorMessage("")}
					title="Errors"
					centered
				>
					{errorMessage}
				</Modal>
				<Box sx={{ maxWidth: "90vw", cursor: isLoading ? "wait" : "auto" }}>
					{isLoading ? (
						<Loader size="xl" />
					) : !song ? (
						<Text>Song data did not load correctly.</Text>
					) : (
						<>
							<Group mb="md" align="center">
								<Text>Upload a new MusicXML:</Text>
								<input
									type="file"
									accept=".musicxml"
									style={{ margin: "10px" }}
									onChange={(e) =>
										handleMusicXMLFileChange(e.target.files?.[0] || null)
									}
									required
								/>
								<Button
									onClick={() => {
										processSongData();
										MidiPlayer.stop();
									}}
									disabled={!songPartList || (songPartList?.parts.length ?? 0) < 0}
								>
									Process Song Data
								</Button>
							</Group>
							{songPartList && (
								<Box sx={{ position: "relative" }}>
									{/* Section labels */}
									<Group
										sx={{
											position: "absolute",
											top: 0,
											left: PART_LABEL_WIDTH,
											height: SECTION_LABEL_HEIGHT,
											zIndex: 2,
										}}
									>
										{songPartList.parts.length > 0 &&
											songPartList.parts[0].getSectionInfo().map(({ sectionName, sectionLength }) => (
												<Box
													key={sectionName + "-label"}
													sx={{
														width: sectionLength * measureWidth,
														overflow: "hidden",
														whiteSpace: "nowrap",
														textOverflow: "ellipsis"
													}}
												>
													<Text size="xs">{sectionName}</Text>
												</Box>
											))}
									</Group>
									{/* Song processor area */}
									<Paper
										ref={librarySectionContainerRef}
										onWheel={handleLibraryZoom}
										sx={{
											height: songPartList.parts.length * partHeight,
											overflow: "auto",
											backgroundColor: "#f0f0f0",
											backgroundImage: `url('../assets/images/conduction-logo.svg')`,
											backgroundRepeat: "no-repeat",
											backgroundPosition: "right top",
											backgroundSize: "330px",
											position: "relative",
											top: SECTION_LABEL_HEIGHT
										}}
									>
										{songPartList.parts.length > 0 && (
											<Box
												sx={{
													position: "relative",
													width: songPartList.getLength() * measureWidth,
												}}
											>
												{songPartList.parts.map((part, i) => (
													<LibraryPart
														key={part.id}
														partIndex={i}
														part={part}
														height={partHeight}
														measureWidth={measureWidth}
														selectedSectionIds={selectedSectionsIds}
														selectSectionId={handleBoxSelect}
														deselectSectionId={handleBoxSelect}
														clearSelection={() => setSelectedSectionsIds([])}
														dragging={false}
													/>
												))}
											</Box>
										)}
									</Paper>
								</Box>
							)}
						</>
					)}
				</Box>
			</Box>
		</ConductionModal>
	);
};

export default SongProcessor;
